import { Signal } from 'typed-signals';
import type { Container, Sprite, Text } from 'pixi.js';
import type { TournamentPageLayout } from '../../../layouts/mainMenu/tournament/TournamentPageLayout';
import { centerElements } from '../../game/utils/centerElements';
import { TournamentCategory } from '../../app/tournaments/TournamentService';

interface TabElements {
  tab: Container;
  bg: Sprite;
  label: Text;
  icon: Sprite;
}

export class TournamentTabBarView {
  public readonly onTabClicked = new Signal<(tab: TournamentCategory) => void>();

  private currentTab: TournamentCategory | null = null;

  constructor(
    private readonly layout: TournamentPageLayout,
    initTab: TournamentCategory,
  ) {
    this.init();
    this.setActiveTab(initTab ?? TournamentCategory.Global);
  }

  private init(): void {
    Object.values(TournamentCategory).forEach((tournamentTab: TournamentCategory) => {
      const { icon, label, tab } = this.getTabElements(tournamentTab);
      tab.on('pointerdown', () => {
        this.onTabClicked.emit(tournamentTab);
      });
      centerElements([icon, label], 5);
    });
  }

  public setActiveTab(selectedTab: TournamentCategory): void {
    if (this.currentTab === selectedTab) return;

    Object.values(TournamentCategory).forEach((tab) => {
      this.refreshTabView(tab, tab === selectedTab);
    });
    this.currentTab = selectedTab;
  }

  private refreshTabView(tournamentTab: TournamentCategory, active: boolean): void {
    const { bg, label, icon } = this.getTabElements(tournamentTab);
    const {
      root: { data },
    } = this.layout;

    bg.props(active ? data.tab.active : data.tab.inactive);
    label.props(active ? data.label.active : data.label.inactive);
    icon.props({
      texture: this.getIconTexture(tournamentTab, active),
      ...(active ? data.icon.active : data.icon.inactive),
    });
  }

  private getIconTexture(tab: TournamentCategory, active: boolean): string {
    const {
      root: { data },
    } = this.layout;

    const icons: Record<TournamentCategory, { active: string; inactive: string }> = {
      [TournamentCategory.Global]: data.icons.global,
      [TournamentCategory.Friends]: data.icons.friends,
      [TournamentCategory.You]: data.icons.you,
    };

    return icons[tab][active ? 'active' : 'inactive'];
  }

  private getTabElements(tab: TournamentCategory): TabElements {
    const {
      globalTab,
      globalTabBackground,
      globalLabel,
      globalIcon,
      friendsTab,
      friendsTabBackground,
      friendsLabel,
      friendsIcon,
      youTab,
      youTabBackground,
      youLabel,
      youIcon,
    } = this.layout;

    const elements: Record<TournamentCategory, TabElements> = {
      [TournamentCategory.Global]: { tab: globalTab, bg: globalTabBackground, label: globalLabel, icon: globalIcon },
      [TournamentCategory.Friends]: {
        tab: friendsTab,
        bg: friendsTabBackground,
        label: friendsLabel,
        icon: friendsIcon,
      },
      [TournamentCategory.You]: { tab: youTab, bg: youTabBackground, label: youLabel, icon: youIcon },
    };

    return elements[tab];
  }
}
